import React, { memo, useCallback } from 'react';
import {
  IconTextAlignLeft,
  IconTextAlignCenter,
  IconTextAlignRight,
} from '../../../icons';
import Tiptap, { useTiptap } from '../Tiptap';
import { type Editor, useEditorState } from '@tiptap/react';
import TiptapToolbarButton from './tiptap-toolbar-button';

const TiptapTextAlignLeft = memo(() => {
  const { editor } = useTiptap();

  const editorState = useEditorState({
    editor,
    selector: ({ editor }: { editor: Editor }) => ({
      isTextAlignLeft: editor.isActive({ textAlign: 'left' }),
    }),
  });

  const onClick = useCallback(
    () => editor.chain().focus().setTextAlign('left').run(),
    [editor],
  );

  return (
    <TiptapToolbarButton
      aria-label="aligner à gauche"
      tooltip="Aligner à gauche"
      icon={IconTextAlignLeft}
      isActive={editorState.isTextAlignLeft}
      onClick={onClick}
    />
  );
});

TiptapTextAlignLeft.displayName = 'Tiptap.Toolbar.TextAlign.Left';

const TiptapTextAlignCenter = memo(() => {
  const { editor } = useTiptap();

  const editorState = useEditorState({
    editor,
    selector: ({ editor }: { editor: Editor }) => ({
      isTextAlignCenter: editor.isActive({ textAlign: 'center' }),
    }),
  });

  const onClick = useCallback(
    () => editor.chain().focus().setTextAlign('center').run(),
    [editor],
  );

  return (
    <TiptapToolbarButton
      aria-label="centrer"
      tooltip="Centrer"
      icon={IconTextAlignCenter}
      isActive={editorState.isTextAlignCenter}
      onClick={onClick}
    />
  );
});

TiptapTextAlignCenter.displayName = 'Tiptap.Toolbar.TextAlign.Center';

const TiptapTextAlignRight = memo(() => {
  const { editor } = useTiptap();

  const editorState = useEditorState({
    editor,
    selector: ({ editor }: { editor: Editor }) => ({
      isTextAlignRight: editor.isActive({ textAlign: 'right' }),
    }),
  });

  const onClick = useCallback(
    () => editor.chain().focus().setTextAlign('right').run(),
    [editor],
  );

  return (
    <TiptapToolbarButton
      aria-label="aligner à droite"
      tooltip="Aligner à droite"
      icon={IconTextAlignRight}
      isActive={editorState.isTextAlignRight}
      onClick={onClick}
    />
  );
});

TiptapTextAlignRight.displayName = 'Tiptap.Toolbar.TextAlign.Right';

const TiptapTextAlignGroup = memo((props: React.ComponentPropsWithoutRef<'div'>) => (
  <Tiptap.Toolbar.Group {...props}>
    <TiptapTextAlignLeft />
    <TiptapTextAlignCenter />
    <TiptapTextAlignRight />
  </Tiptap.Toolbar.Group>
));

TiptapTextAlignGroup.displayName = 'Tiptap.Toolbar.TextAlign';

const TiptapTextAlignGroupCompound = Object.assign(TiptapTextAlignGroup, {
  Left: TiptapTextAlignLeft,
  Center: TiptapTextAlignCenter,
  Right: TiptapTextAlignRight,
});

export default TiptapTextAlignGroupCompound;
